import React, { useEffect } from "react"

const newUuid = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export interface UseOperationInput<TResult, TInput> {
  operation: (variables: TInput) => Promise<TResult>
  onSuccess?: (result: TResult) => void
  onError?: (error: Error) => void
  onFinalized?: () => void
  autoTrigger?: boolean
}

export interface OperationState<TResult> {
  operationId?: string
  loading: boolean
  completed: boolean
  called: boolean
  success?: boolean
  data?: TResult
  error?: any
}

const invokeOperation = async <TResult, TInput>(
  input: UseOperationInput<TResult, TInput>,
  variables?: TInput
): Promise<TResult> => {
  if (variables) {
    return await (input.operation as (variables: TInput) => Promise<TResult>)(
      variables
    )
  }
  return await (input.operation as () => Promise<TResult>)()
}

export const useOperation = <TResult, TInput>(
  input: UseOperationInput<TResult, TInput>
) => {
  const [state, setState] = React.useState<OperationState<TResult>>({
    called: false,
    loading: false,
    completed: false,
  })

  const invoke = async (variables?: TInput): Promise<TResult | undefined> => {
    const operationId = newUuid()
    setState({
      called: true,
      loading: true,
      completed: false,
      data: undefined,
      error: undefined,
      operationId,
    })
    try {
      const result = await invokeOperation(input, variables)
      setState({
        called: true,
        loading: false,
        data: result,
        error: undefined,
        operationId,
        completed: true,
        success: true,
      })
      input.onSuccess?.(result)
      return result
    } catch (e: any) {
      console.error("Error invoking API", e)
      setState({
        called: true,
        loading: false,
        data: undefined,
        error: e,
        operationId,
        completed: true,
        success: false,
      })
      input.onError?.(e)
      throw e
    } finally {
      input.onFinalized?.()
    }
  }

  useEffect(() => {
    if (input.autoTrigger) {
      invoke()
    }
  }, [])

  return {
    invoke,
    state,
  }
}
